import { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, RadarChart, Radar,
  PolarGrid, PolarAngleAxis,
} from "recharts";
import {
  TrendingUp, Activity, Package, CheckCircle2, BarChart2,
  RefreshCw, Loader2, Zap, Search, Image, ArrowRight,
  AlertTriangle, Info, ArrowUpRight, ArrowDownRight,
  Clock, GitBranch, Target, Sparkles, ChevronRight,
  TrendingDown, ShoppingBag, Star,
} from "lucide-react";

// ── Constants ──────────────────────────────────────────────────────────────
const COLORS = {
  success: "#10B981",
  failed: "#EF4444",
  partial: "#F59E0B",
  pending: "#94A3B8",
  blue: "#004EEB",
  indigo: "#6366F1",
  pink: "#EC4899",
};

const PIE_COLORS = {
  success: "#10B981",
  failed: "#EF4444",
  partial: "#F59E0B",
  pending: "#94A3B8",
  null: "#94A3B8",
};

const STATUS_LABELS = {
  success: "نجح",
  failed: "فشل",
  partial: "جزئي",
  pending: "بانتظار",
};

const TRIGGER_LABELS = {
  manual: "يدوي",
  cron: "مجدول",
  webhook: "Webhook",
  other: "آخر",
};

const TRIGGER_COLORS = ["#004EEB", "#10B981", "#8B5CF6", "#F59E0B"];

const DAYS_OPTIONS = [
  { value: 7, label: "٧ أيام" },
  { value: 30, label: "٣٠ يوماً" },
  { value: 90, label: "٩٠ يوماً" },
];

const TABS = [
  { id: "sync", label: "التزامن", icon: Activity },
  { id: "seo", label: "SEO", icon: Search },
  { id: "inventory", label: "المخزون", icon: Package },
  { id: "insights", label: "رؤى AI", icon: Sparkles },
];

// ── Helper: format seconds ─────────────────────────────────────────────────
function fmtDuration(sec) {
  if (!sec) return "—";
  if (sec < 60) return `${sec}ث`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s > 0 ? `${m}د ${s}ث` : `${m} دقيقة`;
}

// ── Animated counter hook ──────────────────────────────────────────────────
function useCounter(target, duration = 800) {
  const [value, setValue] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    if (target == null || isNaN(target)) return;
    const start = Date.now();
    const tick = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * target));
      if (progress < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration]);
  return value;
}

// ── KPI Card ───────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, icon: Icon, tone = "blue", delta, testid, suffix = "" }) {
  const tones = {
    blue:    { bar: "bg-[#004EEB]",   icon: "text-[#004EEB] bg-[#004EEB]/10",   val: "text-[#004EEB]" },
    emerald: { bar: "bg-emerald-500", icon: "text-emerald-600 bg-emerald-50",    val: "text-emerald-600" },
    amber:   { bar: "bg-amber-500",   icon: "text-amber-600 bg-amber-50",        val: "text-amber-600" },
    indigo:  { bar: "bg-indigo-500",  icon: "text-indigo-600 bg-indigo-50",      val: "text-indigo-600" },
    pink:    { bar: "bg-pink-500",    icon: "text-pink-600 bg-pink-50",          val: "text-pink-600" },
  };
  const t = tones[tone];
  const numVal = typeof value === "number" ? value : null;
  const animated = useCounter(numVal);
  const display = numVal !== null ? `${animated.toLocaleString("ar-SA")}${suffix}` : (value ?? "—");

  return (
    <div data-testid={testid}
      className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] p-5 relative overflow-hidden group hover:-translate-y-0.5 transition-all duration-300 shadow-sm hover:shadow-md">
      <div className={`absolute bottom-0 start-0 h-[3px] ${t.bar} transition-all duration-500 w-0 group-hover:w-full rounded-b-2xl`} />
      <div className="flex items-start justify-between mb-3">
        <span className="text-[10px] font-bold text-[var(--text-muted)] tracking-[0.14em] uppercase leading-tight">{label}</span>
        <div className={`w-9 h-9 rounded-xl grid place-items-center ${t.icon} shrink-0 transition-transform group-hover:scale-110`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className={`text-[2.1rem] font-black leading-none ${t.val} tabular-nums`}>{display}</div>
      <div className="flex items-center justify-between mt-2">
        {sub && <p className="text-[10px] text-[var(--text-muted)]">{sub}</p>}
        {delta != null && (
          <span className={`flex items-center gap-0.5 text-[10px] font-bold ${delta >= 0 ? "text-emerald-600" : "text-red-500"}`}>
            {delta >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {Math.abs(delta)}%
          </span>
        )}
      </div>
    </div>
  );
}

// ── Chart Card ─────────────────────────────────────────────────────────────
function ChartCard({ title, subtitle, children, className = "", action }) {
  return (
    <div className={`bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] overflow-hidden ${className}`}>
      <div className="px-6 py-4 border-b border-[var(--border-color)] flex items-center justify-between bg-[var(--bg-card-hover)]">
        <div>
          <h3 className="text-sm font-bold text-[var(--text-primary)]">{title}</h3>
          {subtitle && <p className="text-[11px] text-[var(--text-muted)] mt-0.5">{subtitle}</p>}
        </div>
        {action}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

// ── Dark Tooltip ───────────────────────────────────────────────────────────
function DarkTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0C0C0F] border border-white/10 rounded-xl px-4 py-3 text-xs shadow-xl" dir="rtl">
      <div className="text-white/50 mb-2 font-semibold">{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 text-white/80 mt-1">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: p.color }} />
          <span>{p.name}:</span>
          <span className="font-bold">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

// ── Empty State ────────────────────────────────────────────────────────────
function EmptyState({ icon: Icon, title, desc, action, onAction }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
      <div className="w-14 h-14 rounded-2xl bg-slate-100 grid place-items-center mb-4">
        <Icon className="w-6 h-6 text-[var(--text-muted)]" />
      </div>
      <h4 className="font-bold text-[var(--text-secondary)] mb-1">{title}</h4>
      <p className="text-sm text-[var(--text-muted)] max-w-xs mb-5">{desc}</p>
      {action && (
        <button onClick={onAction}
          className="flex items-center gap-2 px-4 py-2 bg-[#004EEB] text-white text-sm font-semibold rounded-xl hover:bg-[#003bb5] transition-colors">
          {action}
          <ChevronRight className="w-4 h-4 rtl-flip" />
        </button>
      )}
    </div>
  );
}

// ── Score Ring ─────────────────────────────────────────────────────────────
function ScoreRing({ score, size = 88, strokeWidth = 8 }) {
  const r = (size - strokeWidth) / 2;
  const circ = 2 * Math.PI * r;
  const pct = (score || 0) / 100;
  const color = score >= 75 ? "#10B981" : score >= 50 ? "#F59E0B" : "#EF4444";
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="rotate-[-90deg]">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#F1F5F9" strokeWidth={strokeWidth} />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke={color} strokeWidth={strokeWidth}
        strokeDasharray={`${circ * pct} ${circ}`}
        strokeLinecap="round"
        style={{ transition: "stroke-dasharray 1s ease" }}
      />
    </svg>
  );
}

// ── Insight Card ───────────────────────────────────────────────────────────
const INSIGHT_STYLES = {
  critical: { bg: "bg-red-50",     border: "border-red-200",   icon: AlertTriangle, iconColor: "text-red-500",   badge: "bg-red-100 text-red-700" },
  warning:  { bg: "bg-amber-50",   border: "border-amber-200", icon: AlertTriangle, iconColor: "text-amber-500", badge: "bg-amber-100 text-amber-700" },
  info:     { bg: "bg-blue-50",    border: "border-blue-200",  icon: Info,          iconColor: "text-blue-500",  badge: "bg-blue-100 text-blue-700" },
  success:  { bg: "bg-emerald-50", border: "border-emerald-200", icon: CheckCircle2, iconColor: "text-emerald-500", badge: "bg-emerald-100 text-emerald-700" },
};
const INSIGHT_TYPE_LABEL = { critical: "عاجل", warning: "تحذير", info: "معلومة", success: "ممتاز" };

function InsightCard({ insight, onNavigate }) {
  const s = INSIGHT_STYLES[insight.type] || INSIGHT_STYLES.info;
  const IconComp = s.icon;
  return (
    <div className={`${s.bg} ${s.border} border rounded-2xl p-4 flex items-start gap-3 hover:shadow-sm transition-shadow`}>
      <div className={`w-8 h-8 rounded-xl bg-[var(--bg-card)] grid place-items-center shrink-0 shadow-sm`}>
        <IconComp className={`w-4 h-4 ${s.iconColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${s.badge}`}>
            {INSIGHT_TYPE_LABEL[insight.type] || insight.type}
          </span>
        </div>
        <p className="text-sm font-medium text-[var(--text-primary)] leading-relaxed">{insight.text}</p>
        {insight.action && insight.link && (
          <button
            onClick={() => onNavigate(insight.link)}
            className={`mt-2 flex items-center gap-1 text-xs font-bold ${s.iconColor} hover:underline`}
          >
            {insight.action}
            <ArrowRight className="w-3 h-3 rtl-flip" />
          </button>
        )}
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function Analytics() {
  const [data, setData] = useState(null);
  const [aiInsights, setAiInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [activeTab, setActiveTab] = useState("sync");
  const navigate = useNavigate();

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const [analyticsRes, dashRes] = await Promise.allSettled([
        api.get(`/analytics/dashboard?days=${d}`),
        api.get("/dashboard/intelligence"),
      ]);
      if (analyticsRes.status === "fulfilled") setData(analyticsRes.value.data);
      if (dashRes.status === "fulfilled") setAiInsights(dashRes.value.data?.insights || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  const summary = data?.summary || {};

  // Build trigger distribution for pie
  const triggerData = Object.entries(data?.trigger_distribution || {})
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: TRIGGER_LABELS[k] || k, value: v }));

  // Sync score badge
  const successRate = summary.success_rate ?? 0;
  const syncColor = successRate >= 80 ? "text-emerald-600" : successRate >= 50 ? "text-amber-600" : "text-red-500";

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-page)" }} dir="rtl">
      <div className="p-5 md:p-8 max-w-[1400px] mx-auto space-y-6">

        {/* ── Header ── */}
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[11px] tracking-[0.18em] uppercase text-[var(--text-muted)] font-bold flex items-center gap-2">
              <BarChart2 className="w-3 h-3" />
              التقارير والتحليلات
            </p>
            <h1 className="text-2xl md:text-3xl font-black tracking-tight text-[var(--text-primary)] mt-1">
              مركز التحليلات
            </h1>
            <p className="text-[var(--text-secondary)] mt-1 text-sm">رؤية شاملة لأداء متجرك وعمليات التزامن والـ SEO.</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex bg-[var(--bg-card)] border border-[var(--border-color)] rounded-xl p-1 gap-1">
              {DAYS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setDays(opt.value)}
                  data-testid={`days-filter-${opt.value}`}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    days === opt.value
                      ? "bg-[#004EEB] text-white shadow-sm"
                      : "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-card-hover)]"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => load(days)}
              disabled={loading}
              data-testid="analytics-refresh-btn"
              className="p-2.5 bg-[var(--bg-card)] border border-[var(--border-color)] rounded-xl text-[var(--text-secondary)] hover:text-[#004EEB] hover:border-[#004EEB]/30 transition-colors"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </button>
          </div>
        </header>

        {/* ── KPI Cards ── */}
        <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="analytics-summary">
          <KpiCard
            label="إجمالي عمليات التزامن"
            value={summary.total_syncs}
            sub="منذ البداية"
            icon={Activity}
            tone="blue"
            testid="kpi-total-syncs"
          />
          <KpiCard
            label="معدل النجاح"
            value={summary.success_rate}
            suffix="%"
            sub={`آخر ${days} يوماً`}
            icon={TrendingUp}
            tone="emerald"
            testid="kpi-success-rate"
          />
          <KpiCard
            label="المنتجات المُزامَنة"
            value={summary.total_products_synced}
            sub={`آخر ${days} يوماً`}
            icon={CheckCircle2}
            tone="indigo"
            testid="kpi-products-synced"
          />
          <KpiCard
            label="إجمالي المنتجات"
            value={summary.total_products}
            sub="في قاعدة البيانات"
            icon={Package}
            tone="amber"
            testid="kpi-total-products"
          />
          <KpiCard
            label="درجة SEO الحالية"
            value={summary.latest_seo_score != null ? summary.latest_seo_score : "—"}
            suffix={summary.latest_seo_score != null ? "/100" : ""}
            sub="آخر تحليل"
            icon={Search}
            tone="pink"
            testid="kpi-seo-score"
          />
          <KpiCard
            label="تحسينات مطبّقة"
            value={summary.seo_applied}
            sub="اقتراحات SEO"
            icon={Zap}
            tone="blue"
            testid="kpi-seo-applied"
          />
        </section>

        {/* ── Tabs ── */}
        <div className="flex gap-1 bg-[var(--bg-card)] border border-[var(--border-color)] rounded-2xl p-1.5 w-fit" data-testid="analytics-tabs">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all duration-200 ${
                  isActive
                    ? "bg-[#004EEB] text-white shadow-sm"
                    : "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-card-hover)]"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* ═══════════════════════════════════════════════════════════════════
            TAB: التزامن
        ═══════════════════════════════════════════════════════════════════ */}
        {activeTab === "sync" && (
          <div className="space-y-6">

            {/* Sync Activity Daily */}
            <ChartCard
              title="نشاط التزامن اليومي"
              subtitle={`الناجح والفاشل خلال آخر ${days} يوماً`}
            >
              {loading ? (
                <div className="h-64 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-300" />
                </div>
              ) : !data?.sync_history?.length ? (
                <EmptyState
                  icon={Activity}
                  title="لا توجد عمليات تزامن"
                  desc="لم يتم تسجيل أي عملية تزامن في الفترة المختارة."
                  action="ابدأ التزامن"
                  onAction={() => navigate("/dashboard")}
                />
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={data.sync_history} barCategoryGap="30%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                    <XAxis dataKey="date" tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => v?.slice(5)} />
                    <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip content={<DarkTooltip />} />
                    <Legend formatter={(v) => <span className="text-xs text-[var(--text-secondary)]">{v}</span>} />
                    <Bar dataKey="success" name="نجح" stackId="a" fill={COLORS.success} radius={[0, 0, 0, 0]} />
                    <Bar dataKey="partial" name="جزئي" stackId="a" fill={COLORS.partial} />
                    <Bar dataKey="failed" name="فشل" stackId="a" fill={COLORS.failed} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Success Rate Trend */}
              <ChartCard title="منحنى معدل النجاح" subtitle="آخر 20 عملية تزامن" className="lg:col-span-2">
                {loading ? (
                  <div className="h-52 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
                ) : !data?.success_rate_trend?.length ? (
                  <EmptyState icon={TrendingUp} title="لا توجد بيانات" desc="قم بإجراء عملية تزامن أولاً." />
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={data.success_rate_trend}>
                      <defs>
                        <linearGradient id="rateGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#004EEB" stopOpacity={0.15} />
                          <stop offset="95%" stopColor="#004EEB" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                      <XAxis dataKey="index" tick={{ fill: "#94A3B8", fontSize: 10 }} axisLine={false} tickLine={false} />
                      <YAxis domain={[0, 100]} tick={{ fill: "#94A3B8", fontSize: 10 }} axisLine={false} tickLine={false} unit="%" />
                      <Tooltip content={<DarkTooltip />} />
                      <Area type="monotone" dataKey="rate" name="معدل النجاح %" stroke="#004EEB" strokeWidth={2.5} fill="url(#rateGrad)" dot={{ fill: "#004EEB", r: 3, strokeWidth: 0 }} activeDot={{ r: 5 }} />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>

              {/* Trigger Distribution */}
              <ChartCard title="مصادر التزامن" subtitle="توزيع حسب نوع التشغيل">
                {loading ? (
                  <div className="h-52 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
                ) : !triggerData.length ? (
                  <EmptyState icon={Target} title="لا توجد بيانات" desc="لا توجد عمليات تزامن بعد." />
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={triggerData} cx="50%" cy="45%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value" nameKey="name" label={({ percent }) => `${(percent * 100).toFixed(0)}%`} labelLine={false}>
                        {triggerData.map((_, i) => (
                          <Cell key={i} fill={TRIGGER_COLORS[i % TRIGGER_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ background: "#0C0C0F", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} itemStyle={{ color: "rgba(255,255,255,0.8)" }} />
                      <Legend formatter={(v) => <span className="text-xs text-[var(--text-secondary)]">{v}</span>} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </div>

            {/* Avg Duration + Products Synced row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Products Synced Timeline */}
              {data?.sync_history?.length > 0 && (
                <ChartCard title="المنتجات المُزامَنة يومياً" subtitle="عدد المنتجات المحدّثة في كل يوم">
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={data.sync_history}>
                      <defs>
                        <linearGradient id="prodGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#6366F1" stopOpacity={0.2} />
                          <stop offset="95%" stopColor="#6366F1" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                      <XAxis dataKey="date" tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => v?.slice(5)} />
                      <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
                      <Tooltip content={<DarkTooltip />} />
                      <Area type="monotone" dataKey="products_synced" name="منتجات مُزامَنة" stroke="#6366F1" strokeWidth={2} fill="url(#prodGrad)" dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </ChartCard>
              )}

              {/* Sync Stats mini cards */}
              <div className="grid grid-cols-2 gap-4 content-start">
                <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] p-5 text-center" data-testid="stat-avg-duration">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 grid place-items-center mx-auto mb-3">
                    <Clock className="w-5 h-5 text-blue-500" />
                  </div>
                  <div className="text-xl font-black text-[var(--text-primary)]">{fmtDuration(data?.avg_duration_sec)}</div>
                  <p className="text-[11px] text-[var(--text-muted)] mt-1">متوسط مدة التزامن</p>
                </div>
                <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] p-5 text-center" data-testid="stat-trigger-manual">
                  <div className="w-10 h-10 rounded-xl bg-indigo-50 grid place-items-center mx-auto mb-3">
                    <Target className="w-5 h-5 text-indigo-500" />
                  </div>
                  <div className="text-xl font-black text-[var(--text-primary)]">
                    {(data?.trigger_distribution?.manual || 0).toLocaleString("ar-SA")}
                  </div>
                  <p className="text-[11px] text-[var(--text-muted)] mt-1">تزامن يدوي</p>
                </div>
                <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] p-5 text-center" data-testid="stat-trigger-cron">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 grid place-items-center mx-auto mb-3">
                    <RefreshCw className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div className="text-xl font-black text-[var(--text-primary)]">
                    {(data?.trigger_distribution?.cron || 0).toLocaleString("ar-SA")}
                  </div>
                  <p className="text-[11px] text-[var(--text-muted)] mt-1">تزامن مجدول</p>
                </div>
                <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] p-5 text-center" data-testid="stat-trigger-webhook">
                  <div className="w-10 h-10 rounded-xl bg-purple-50 grid place-items-center mx-auto mb-3">
                    <Zap className="w-5 h-5 text-purple-500" />
                  </div>
                  <div className="text-xl font-black text-[var(--text-primary)]">
                    {(data?.trigger_distribution?.webhook || 0).toLocaleString("ar-SA")}
                  </div>
                  <p className="text-[11px] text-[var(--text-muted)] mt-1">Webhook</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            TAB: SEO
        ═══════════════════════════════════════════════════════════════════ */}
        {activeTab === "seo" && (
          <div className="space-y-6">

            {/* SEO Score + ALT Coverage summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* SEO Score Ring Card */}
              <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] p-6 flex flex-col items-center justify-center gap-3 shadow-sm" data-testid="seo-score-ring-card">
                <p className="text-[11px] font-bold text-[var(--text-muted)] tracking-widest uppercase">درجة SEO الحالية</p>
                <div className="relative">
                  <ScoreRing score={summary.latest_seo_score} size={110} strokeWidth={10} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className={`text-2xl font-black ${
                      (summary.latest_seo_score || 0) >= 75 ? "text-emerald-600" :
                      (summary.latest_seo_score || 0) >= 50 ? "text-amber-600" : "text-red-500"
                    }`}>
                      {summary.latest_seo_score ?? "—"}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-[var(--text-muted)]">من 100</p>
              </div>

              {/* ALT Coverage */}
              <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] p-6 flex flex-col items-center justify-center gap-3 shadow-sm" data-testid="alt-coverage-card">
                <p className="text-[11px] font-bold text-[var(--text-muted)] tracking-widest uppercase">تغطية صور ALT</p>
                <div className="relative">
                  <ScoreRing score={data?.seo_stats?.alt_coverage_pct} size={110} strokeWidth={10} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-black text-pink-600">
                      {data?.seo_stats?.alt_coverage_pct ?? "—"}
                      {data?.seo_stats?.alt_coverage_pct != null && "%"}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-[var(--text-muted)]">
                  {data?.seo_stats?.alt_with_text ?? 0} / {data?.seo_stats?.alt_total_images ?? 0} صورة
                </p>
              </div>

              {/* Suggestions Stats */}
              <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] p-6 space-y-4 shadow-sm" data-testid="seo-suggestions-card">
                <p className="text-[11px] font-bold text-[var(--text-muted)] tracking-widest uppercase">اقتراحات SEO</p>
                <div className="space-y-3">
                  {[
                    { label: "إجمالي الاقتراحات", val: data?.seo_stats?.total_suggestions ?? 0, color: "bg-blue-500" },
                    { label: "مطبّقة", val: data?.seo_stats?.applied ?? 0, color: "bg-emerald-500" },
                    { label: "معلّقة", val: data?.seo_stats?.pending ?? 0, color: "bg-amber-500" },
                    { label: "ALT نصوص مطبّقة", val: data?.seo_stats?.alt_applied_count ?? 0, color: "bg-pink-500" },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${item.color}`} />
                        <span className="text-xs text-[var(--text-secondary)]">{item.label}</span>
                      </div>
                      <span className="text-sm font-black text-[var(--text-primary)]">{item.val.toLocaleString("ar-SA")}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* SEO Score History */}
            <ChartCard
              title="تاريخ درجة SEO"
              subtitle="تطوّر الدرجة عبر التحليلات السابقة"
              action={
                <button onClick={() => navigate("/optimizer")}
                  className="flex items-center gap-1.5 text-xs font-bold text-[#004EEB] hover:underline">
                  تحليل جديد <ArrowRight className="w-3 h-3 rtl-flip" />
                </button>
              }
            >
              {loading ? (
                <div className="h-64 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-300" /></div>
              ) : !data?.seo_history?.length ? (
                <EmptyState
                  icon={Search}
                  title="لا يوجد تاريخ SEO بعد"
                  desc="أجرِ تحليل SEO لمتجرك لتتبع تطور الدرجة عبر الزمن."
                  action="ابدأ تحليل SEO"
                  onAction={() => navigate("/optimizer")}
                />
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={data.seo_history}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                    <XAxis dataKey="date" tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis domain={[0, 100]} tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip content={<DarkTooltip />} />
                    <Line type="monotone" dataKey="score" name="درجة SEO" stroke="#EC4899" strokeWidth={3} dot={{ fill: "#EC4899", r: 5, strokeWidth: 0 }} activeDot={{ r: 7 }} />
                    <Line type="monotone" dataKey="products_with_issues" name="منتجات تحتاج تحسين" stroke="#F59E0B" strokeWidth={2} strokeDasharray="5 5" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            {/* SEO Improvement Progress Bar */}
            {(data?.seo_stats?.total_suggestions || 0) > 0 && (
              <ChartCard title="تقدم تحسينات SEO" subtitle="نسبة الاقتراحات المطبّقة مقابل الكلية">
                <div className="py-4 px-2 space-y-4">
                  {[
                    {
                      label: "اقتراحات العناوين والأوصاف",
                      applied: data?.seo_stats?.applied || 0,
                      total: data?.seo_stats?.total_suggestions || 0,
                      color: "bg-[#004EEB]",
                    },
                    {
                      label: "نصوص ALT للصور",
                      applied: data?.seo_stats?.alt_applied_count || 0,
                      total: data?.seo_stats?.alt_total_images || 0,
                      color: "bg-pink-500",
                    },
                  ].map((item) => {
                    const pct = item.total > 0 ? Math.round((item.applied / item.total) * 100) : 0;
                    return (
                      <div key={item.label}>
                        <div className="flex justify-between text-xs font-semibold text-[var(--text-secondary)] mb-2">
                          <span>{item.label}</span>
                          <span>{item.applied.toLocaleString("ar-SA")} / {item.total.toLocaleString("ar-SA")} ({pct}%)</span>
                        </div>
                        <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full ${item.color} rounded-full transition-all duration-1000`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ChartCard>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            TAB: المخزون
        ═══════════════════════════════════════════════════════════════════ */}
        {activeTab === "inventory" && (
          <div className="space-y-6">

            {/* Low Stock Alert Banner */}
            {(data?.low_stock_count || 0) > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center gap-4" data-testid="low-stock-banner">
                <div className="w-10 h-10 rounded-xl bg-amber-100 grid place-items-center shrink-0">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-amber-800 text-sm">
                    {data.low_stock_count.toLocaleString("ar-SA")} منتج بمخزون منخفض (أقل من 5 قطع)
                  </p>
                  <p className="text-xs text-amber-600 mt-0.5">راجع مستويات المخزون وأعد التوزيع بين الفروع.</p>
                </div>
                <button onClick={() => navigate("/products")}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white text-xs font-bold rounded-xl hover:bg-amber-700 transition-colors shrink-0">
                  عرض المنتجات <ArrowRight className="w-3 h-3 rtl-flip" />
                </button>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              {/* Product Status Pie */}
              <ChartCard title="حالة المنتجات" subtitle="توزيع آخر مزامنة" className="lg:col-span-2">
                {loading ? (
                  <div className="h-52 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
                ) : !data?.product_distribution?.length ? (
                  <EmptyState icon={Package} title="لا توجد بيانات منتجات" desc="استورد منتجاتك أولاً." />
                ) : (
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie data={data.product_distribution} cx="50%" cy="45%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="count" nameKey="status" label={({ percent }) => `${(percent * 100).toFixed(0)}%`} labelLine={false}>
                        {data.product_distribution.map((entry) => (
                          <Cell key={entry.status} fill={PIE_COLORS[entry.status] || PIE_COLORS.pending} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v, n) => [v, STATUS_LABELS[n] || n]} contentStyle={{ background: "#0C0C0F", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12 }} itemStyle={{ color: "rgba(255,255,255,0.8)" }} />
                      <Legend formatter={(v) => <span className="text-xs text-[var(--text-secondary)]">{STATUS_LABELS[v] || v}</span>} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>

              {/* Branch Stock */}
              <ChartCard title="المخزون حسب الفرع" subtitle="توزيع المخزون الإجمالي لكل فرع" className="lg:col-span-3">
                {loading ? (
                  <div className="h-52 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
                ) : !data?.branch_stock?.length ? (
                  <EmptyState icon={GitBranch} title="لا توجد بيانات فروع" desc="أضف فروعك وزامن مخزونها." action="إدارة الفروع" onAction={() => navigate("/branches")} />
                ) : (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart layout="vertical" data={data.branch_stock} margin={{ right: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
                      <XAxis type="number" tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="name" tick={{ fill: "#64748B", fontSize: 12 }} axisLine={false} tickLine={false} width={90} />
                      <Tooltip content={<DarkTooltip />} />
                      <Bar dataKey="stock" name="المخزون" radius={[0, 6, 6, 0]}>
                        {data.branch_stock.map((_, i) => (
                          <Cell key={i} fill={i === 0 ? "#004EEB" : i === 1 ? "#6366F1" : "#93C5FD"} opacity={1 - i * 0.06} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </div>

            {/* Branch Stock Detail Table */}
            {(data?.branch_stock?.length || 0) > 0 && (
              <ChartCard title="تفاصيل المخزون بالفروع" subtitle="ترتيب تنازلي حسب الكمية">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="branch-stock-table">
                    <thead>
                      <tr className="text-right">
                        <th className="pb-3 text-[10px] font-bold text-[var(--text-muted)] tracking-widest uppercase">الفرع</th>
                        <th className="pb-3 text-[10px] font-bold text-[var(--text-muted)] tracking-widest uppercase text-center">عدد المنتجات</th>
                        <th className="pb-3 text-[10px] font-bold text-[var(--text-muted)] tracking-widest uppercase text-center">إجمالي المخزون</th>
                        <th className="pb-3 text-[10px] font-bold text-[var(--text-muted)] tracking-widest uppercase text-center">النسبة</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {data.branch_stock.map((branch, i) => {
                        const totalStock = data.branch_stock.reduce((s, b) => s + b.stock, 0);
                        const pct = totalStock > 0 ? Math.round((branch.stock / totalStock) * 100) : 0;
                        return (
                          <tr key={i} className="hover:bg-[var(--bg-card-hover)]/60 transition-colors">
                            <td className="py-3 font-semibold text-[var(--text-primary)]">
                              <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full" style={{ background: i === 0 ? "#004EEB" : i === 1 ? "#6366F1" : "#93C5FD" }} />
                                {branch.name}
                              </div>
                            </td>
                            <td className="py-3 text-center text-[var(--text-secondary)]">{(branch.products || 0).toLocaleString("ar-SA")}</td>
                            <td className="py-3 text-center font-bold text-[var(--text-primary)]">{branch.stock.toLocaleString("ar-SA")}</td>
                            <td className="py-3 text-center">
                              <div className="flex items-center gap-2 justify-center">
                                <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-[#004EEB] rounded-full" style={{ width: `${pct}%` }} />
                                </div>
                                <span className="text-xs text-[var(--text-secondary)]">{pct}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </ChartCard>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            TAB: رؤى AI
        ═══════════════════════════════════════════════════════════════════ */}
        {activeTab === "insights" && (
          <div className="space-y-6">

            {/* Header insight banner */}
            <div className="relative bg-[#080F1A] rounded-2xl p-6 overflow-hidden" data-testid="ai-insights-banner">
              <div className="absolute inset-0 opacity-20"
                style={{ background: "radial-gradient(ellipse at 30% 50%, #004EEB 0%, transparent 60%)" }} />
              <div className="relative flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-[#004EEB]/20 border border-[#004EEB]/30 grid place-items-center shrink-0">
                  <Sparkles className="w-6 h-6 text-[#5b93ff]" />
                </div>
                <div>
                  <h2 className="text-white font-black text-lg">رؤى الذكاء الاصطناعي</h2>
                  <p className="text-white/50 text-sm mt-0.5">توصيات مبنية على بيانات متجرك الحقيقية</p>
                </div>
                <div className="ms-auto">
                  <span className="px-3 py-1.5 rounded-full bg-[#004EEB]/20 text-[#5b93ff] text-xs font-bold border border-[#004EEB]/30">
                    {aiInsights.length} توصية
                  </span>
                </div>
              </div>
            </div>

            {/* Insights list */}
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-slate-300" />
              </div>
            ) : !aiInsights.length ? (
              <EmptyState
                icon={Sparkles}
                title="لا توجد رؤى بعد"
                desc="أجرِ تحليل SEO وابدأ بالمزامنة لتظهر رؤى الذكاء الاصطناعي."
                action="ابدأ التحليل"
                onAction={() => navigate("/optimizer")}
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="ai-insights-list">
                {aiInsights.map((insight, i) => (
                  <InsightCard key={i} insight={insight} onNavigate={navigate} />
                ))}
              </div>
            )}

            {/* Store Strength Quick View */}
            <ChartCard
              title="قوة المتجر الرقمية"
              subtitle="نظرة سريعة على محاور أداء متجرك"
              action={
                <button onClick={() => navigate("/dashboard")}
                  className="flex items-center gap-1 text-xs font-bold text-[#004EEB] hover:underline">
                  التفاصيل <ArrowRight className="w-3 h-3 rtl-flip" />
                </button>
              }
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-2" data-testid="store-strength-grid">
                {[
                  { label: "أداء SEO", score: summary.latest_seo_score, icon: Search, color: "#3B82F6" },
                  { label: "نجاح التزامن", score: successRate, icon: RefreshCw, color: "#10B981" },
                  { label: "تغطية ALT", score: data?.seo_stats?.alt_coverage_pct, icon: Image, color: "#EC4899" },
                  {
                    label: "اقتراحات مطبّقة",
                    score: (data?.seo_stats?.total_suggestions || 0) > 0
                      ? Math.round(((data?.seo_stats?.applied || 0) / data.seo_stats.total_suggestions) * 100)
                      : null,
                    icon: Star,
                    color: "#F59E0B",
                  },
                ].map((item, i) => {
                  const IconComp = item.icon;
                  const s = item.score;
                  const c = s == null ? "#94A3B8" : s >= 75 ? "#10B981" : s >= 50 ? "#F59E0B" : "#EF4444";
                  return (
                    <div key={i} className="flex flex-col items-center gap-2 p-4 bg-[var(--bg-card-hover)] rounded-2xl">
                      <div className="relative">
                        <ScoreRing score={s} size={72} strokeWidth={6} />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <IconComp className="w-4 h-4" style={{ color: item.color }} />
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-black" style={{ color: c }}>
                          {s != null ? `${s}%` : "—"}
                        </div>
                        <div className="text-[11px] text-[var(--text-secondary)] font-semibold">{item.label}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ChartCard>
          </div>
        )}

      </div>
    </div>
  );
}
